import type { BookingState } from './types.js';

// How staffed a gig's labor positions are, in headcount slots (a Position
// with quantity 3 is three slots). Denormalized onto Gig.staffing by the
// onStaffingChanged trigger so the gigs list can show it without reading
// every gig's positions and bookings.
export interface GigStaffingSummary {
  // Total slots across all positions.
  needed: number;
  // Slots held by a confirmed person (accepted and beyond).
  filled: number;
  // Slots with an offer out, awaiting the person's response.
  offered: number;
  // Slots with someone assigned who hasn't been sent the offer yet.
  drafted: number;
}

const CONFIRMED: ReadonlySet<BookingState> = new Set(['ACCEPTED', 'BOOKED', 'COMPLETED', 'INVOICED']);

// Per position, each booking state fills at most the slots still unclaimed by
// a "stronger" state (confirmed > offered > draft), and never more than the
// position's quantity — so a double-booked position can't read as over-filled.
// DECLINED / CANCELLED bookings free their slot. quantity <= 0 counts as 1,
// matching nextPositionState.
export function summarizeStaffing(
  positions: Array<{ quantity: number; bookingStates: BookingState[] }>,
): GigStaffingSummary {
  const out: GigStaffingSummary = { needed: 0, filled: 0, offered: 0, drafted: 0 };
  for (const p of positions) {
    const qty = p.quantity > 0 ? p.quantity : 1;
    const confirmed = p.bookingStates.filter(s => CONFIRMED.has(s)).length;
    const offered = p.bookingStates.filter(s => s === 'OFFERED').length;
    const drafted = p.bookingStates.filter(s => s === 'DRAFT').length;
    const filled = Math.min(confirmed, qty);
    const off = Math.min(offered, qty - filled);
    const dr = Math.min(drafted, qty - filled - off);
    out.needed += qty;
    out.filled += filled;
    out.offered += off;
    out.drafted += dr;
  }
  return out;
}

export type StaffingStatus = 'NONE' | 'ALL_FILLED' | 'ALL_OPEN' | 'OFFERS_OUT' | 'TO_SEND' | 'PARTIAL';

// The one-word status the gigs list shows. Precedence for mixed states puts
// what needs attention soonest first: offers awaiting a response, then
// assignments not yet sent, then plain partial coverage.
export function staffingStatus(s: GigStaffingSummary): StaffingStatus {
  if (s.needed <= 0) return 'NONE';
  if (s.filled >= s.needed) return 'ALL_FILLED';
  if (s.filled + s.offered + s.drafted === 0) return 'ALL_OPEN';
  if (s.offered > 0) return 'OFFERS_OUT';
  if (s.drafted > 0) return 'TO_SEND';
  return 'PARTIAL';
}

export function staffingOpenSlots(s: GigStaffingSummary): number {
  return Math.max(0, s.needed - s.filled - s.offered - s.drafted);
}
